import {
  billingDiscountsQuery,
  billingPlansQuery,
  fetchBillingSubscribe,
  fetchBillingUnsubscribe,
  Plan,
} from '@blesk-frontend/api';
import {useBleskAuth, useTeamStaff, useTeamSubscription} from '@blesk-frontend/auth';
import {Button, Crown, getMotionProps, Loader, Page, PageTitle, Switch} from '@blesk-frontend/ui';
import {useQuery} from '@tanstack/react-query';
import {capitalize} from 'es-toolkit';
import {AnimatePresence, motion} from 'motion/react';
import {useMemo, useState} from 'react';

import {GuideListTopbar} from '@/features/guides';
import {DemoRequestFrom, PlanCard} from '@/features/plans';
import {sendEvent} from '@/libs/analytics';
import {formatCurrency} from '@/libs/numbers';
import {CloudPaymentsPayChargeData, useCloudPayments} from '../hooks/useCloudPayments';

const gridMotionProps = getMotionProps({
  initial: {height: 0, scale: 1.02, opacity: 0},
  animate: {height: 'auto', y: 0, scale: 1, opacity: 1},
  exit: {height: 0, scale: 0.98, opacity: 0},
});

const planHumanMap = new Map([
  [
    'free',
    {
      label: 'Blesk Start',
      beforeDiscount: null,
      description: [
        'до пяти инструкций в месяц',
        'до двух пользователей в команде',
        'одна коллекция',
        'лого Блеска в инструкциях',
      ],
    },
  ],
  ['pro/1day', {}],
  [
    'pro/1month',
    {
      label: 'Blesk Pro',
      beforeDiscount: null,
      description: [
        'безлимитные инструкции',
        'до двух пользователей в команде',
        'одна коллекция',
        'нет брендирования Блеска',
      ],
    },
  ],
  [
    'pro/1year',
    {
      label: 'Blesk Pro',
      beforeDiscount: 28800,
      description: [
        'безлимитные инструкции',
        'до двух пользователей в команде',
        'одна коллекция',
        'нет брендирования Блеска',
      ],
    },
  ],
  ['team/1day', {}],
  [
    'team/1month',
    {
      label: 'Blesk Team',
      beforeDiscount: null,
      description: [
        'безлимитные инструкции',
        'безлимитная команда',
        'безлимитные коллекции',
        'нет брендирования Блеска',
      ],
    },
  ],
  [
    'team/1year',
    {
      label: 'Blesk Team',
      beforeDiscount: 86400,
      description: [
        'безлимитные инструкции',
        'безлимитная команда',
        'безлимитные коллекции',
        'нет брендирования Блеска',
      ],
    },
  ],
]);

export const PlansPage = () => {
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
  const [interval, setInterval] = useState<'month' | 'year'>('month');

  const {
    query: {data: user},
  } = useBleskAuth();
  const {staff} = useTeamStaff();
  const isBleskStaff = !staff || staff?.name === 'Blesk';

  const planFooterText = useMemo(
    () => user?.teams.at(0)?.team?.staff?.at(0)?.staff?.planFooterText,
    [user],
  );

  const cloudPayments = useCloudPayments();

  const {data: discounts} = useQuery(billingDiscountsQuery);

  const {
    subscription,
    isLoading: isTeamSubscriptionLoading,
    refetchSubscription,
  } = useTeamSubscription();
  const {data: plans = [], isLoading: isTeamPlanLoading} = useQuery(billingPlansQuery);

  const isLoading = isTeamSubscriptionLoading || isTeamPlanLoading;
  const freePlanHuman = planHumanMap.get('free');

  const welcomeDiscount = useMemo(() => {
    return discounts?.find((discount) => discount.code === 'welcome');
  }, [discounts]);

  const onChangeYearInterval = (checked: boolean) => {
    setInterval(checked ? 'year' : 'month');
  };

  const onClickSubscribe = async (plan: Plan) => {
    if (welcomeDiscount) {
      sendEvent('billing/discount:try-subscribe', {
        plan: plan.name,
        interval: plan.interval,
        discountId: welcomeDiscount.id,
      });
    }

    const order = await fetchBillingSubscribe({planId: plan.id});
    const planHuman = planHumanMap.get(plan.name || 'free');

    const data: CloudPaymentsPayChargeData = {
      CloudPayments: {
        recurrent: {
          interval: capitalize(plan.interval),
          period: plan.period,
        },
        firstName: user?.fullName?.split(' ').at(0) || '',
        lastName: user?.fullName?.split(' ').at(1) || '',
      },
    };

    cloudPayments.oncomplete = () => {
      sendEvent('plans/subscribe', {
        plan: plan.name,
        interval: plan.interval,
      });
      if (welcomeDiscount) {
        sendEvent('billing/discount:subscribe', {
          plan: plan.name,
          interval: plan.interval,
          discountId: welcomeDiscount.id,
        });
      }
      refetchSubscription();
      setTimeout(() => {
        refetchSubscription();
      }, 500);
    };

    cloudPayments.pay('charge', {
      publicId: 'pk_a6c9d07e55f40e8606a2f643d8897',
      email: user?.email,
      accountId: order.teamId,
      invoiceId: order.id,
      description: planHuman?.label || plan.name,
      amount: welcomeDiscount
        ? plan.price - (plan.price * welcomeDiscount.discount) / 100
        : plan.price,
      currency: 'RUB',
      data,
    });
  };

  const onClickUnsubscribe = async () => {
    setUnsubscribeLoading(true);
    try {
      await fetchBillingUnsubscribe();
      await refetchSubscription();
    } finally {
      setUnsubscribeLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Page className="flex h-full flex-col">
        <GuideListTopbar />
        <div className="flex size-full items-center justify-center">
          <Loader />
        </div>
      </Page>
    );
  }

  if (import.meta.env.VITE_ON_PREMISE_CLIENT_ID) {
    return (
      <Page className="flex h-full flex-col">
        <GuideListTopbar />
        <p>Недоступно в On-Premise версии.</p>
      </Page>
    );
  }

  return (
    <Page className="flex h-full flex-col">
      <GuideListTopbar />

      <div className="container mx-auto flex h-full max-w-7xl flex-col gap-8">
        <div>
          <PageTitle>Тарифы и оплата</PageTitle>
        </div>

        <div className="text-paragraph-md text-text-secondary">
          <p>
            <span>Ваш тариф: {planHumanMap.get(subscription?.plan?.name || 'free')?.label}</span>
            {subscription?.plan.interval === 'year' && ' (год)'}
          </p>
          {subscription?.status === 'active' && !!subscription.endedAt && (
            <p>
              Следующее списание{' '}
              {welcomeDiscount
                ? formatCurrency(
                    subscription.plan.price -
                      (subscription.plan.price * welcomeDiscount.discount) / 100,
                  )
                : formatCurrency(subscription.plan.price)}
              руб. будет произведено {new Date(subscription.endedAt).toLocaleDateString('ru-RU')}
            </p>
          )}
          {subscription && subscription?.status !== 'active' && !!subscription.endedAt && (
            <p>
              Подписка отменена, списаний больше не будет
              <br />
              Возможности тарифа доступны до{' '}
              {new Date(subscription.endedAt).toLocaleDateString('ru-RU')}
            </p>
          )}
        </div>

        {subscription?.status === 'active' && isBleskStaff && (
          <div className="flex flex-col items-start gap-6">
            <button
              className="inline-flex cursor-pointer items-start justify-center gap-2 text-center text-brand-primary leading-4 transition-colors disabled:text-text-disabled"
              disabled={unsubscribeLoading}
              type="button"
              onClick={onClickUnsubscribe}
            >
              {unsubscribeLoading && <Loader size={16} />}
              <span>Отменить подписку</span>
            </button>

            <p className="max-w-[700px] text-text-secondary">
              При отмене подписки возможности текущего тарифа будут доступны до конца оплаченного
              периода.
            </p>
          </div>
        )}

        <div className="h-px bg-border-primary" />

        <div className="text-paragraph-md text-text-secondary">
          <p>На всех тарифах доступно:</p>
          <br />
          <ul className="list-disc px-6">
            <li>работа в браузере и десктопе</li>
            <li>маскировка приватных данных</li>
            <li>возможность поделиться инструкцией по ссылке</li>
            <li>экспорт инструкций в другие приложения</li>
          </ul>
        </div>

        <div className="flex flex-col gap-6">
          {!welcomeDiscount && (
            <div className="flex justify-center rounded-lg border bg-bg-secondary p-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="year-interval"
                  checked={interval === 'year'}
                  onCheckedChange={onChangeYearInterval}
                />
                <label className="cursor-pointer text-text-secondary" htmlFor="year-interval">
                  Включить скидку 30% при оплате за год
                </label>
              </div>
            </div>
          )}

          <motion.div layout>
            <AnimatePresence initial={false}>
              <motion.div
                {...gridMotionProps}
                key={`plans-grid-${interval}`}
                className="grid grid-cols-3 gap-6"
              >
                <PlanCard name={freePlanHuman?.label}>
                  <p className="text-title-h4">Бесплатно</p>
                  <ul className="list-disc px-6 text-text-secondary">
                    {freePlanHuman?.description?.map((item, index) => (
                      <li key={`plan-description-${freePlanHuman.label}-${index}`}>{item}</li>
                    ))}
                  </ul>
                  {!subscription && (
                    <div className="mt-auto flex w-fit flex-col gap-2">
                      <Button type="button" disabled={true}>
                        Ваш тариф
                      </Button>
                    </div>
                  )}
                </PlanCard>
                {plans
                  .filter((plan) => planHumanMap.get(plan.name))
                  .filter((plan) => {
                    return plan.interval === interval || plan.status === 'private';
                  })
                  .map((plan) => {
                    const planHuman = planHumanMap.get(plan.name);

                    const discount = planHuman?.beforeDiscount
                      ? ((plan.price - planHuman.beforeDiscount) / planHuman.beforeDiscount) * 100
                      : null;

                    return (
                      <PlanCard
                        key={`plan-${plan.id}`}
                        name={planHuman?.label || plan.name}
                        icon={plan.name.includes('pro') ? Crown : undefined}
                      >
                        {welcomeDiscount && plan.interval === 'month' && (
                          <div className="text-brand-primary text-paragraph-md">
                            Получите скидку {welcomeDiscount.discount}% <br /> при покупке подписки
                            до {new Date(welcomeDiscount.expiresAt).toLocaleDateString('ru-RU')}
                          </div>
                        )}

                        {plan.interval === 'year' && planHuman?.beforeDiscount ? (
                          <p className="text-title-h4">
                            {formatCurrency(plan.price / 12)}{' '}
                            <s className="text-brand-primary">
                              {formatCurrency(planHuman.beforeDiscount / 12)}
                            </s>{' '}
                            руб/мес ({discount}%)
                            <br />
                            при оплате за год
                          </p>
                        ) : (
                          <p className="text-title-h4">
                            {plan.price ? (
                              welcomeDiscount ? (
                                <>
                                  {formatCurrency(
                                    plan.price - (plan.price * welcomeDiscount.discount) / 100,
                                  )}{' '}
                                  <s className="text-brand-primary">{formatCurrency(plan.price)}</s>{' '}
                                  руб/мес
                                </>
                              ) : (
                                `${formatCurrency(plan.price)} руб/мес`
                              )
                            ) : (
                              'Бесплатно'
                            )}
                          </p>
                        )}

                        <ul className="list-disc px-6 text-text-secondary">
                          {planHuman?.description?.map((item, index) => (
                            <li key={`plan-description-${plan.name}-${index}`}>{item}</li>
                          ))}
                        </ul>
                        {!subscription && isBleskStaff && (
                          <div className="mt-auto flex w-fit flex-col gap-2">
                            <Button type="button" onClick={() => onClickSubscribe(plan)}>
                              {plan.interval === 'year' && planHuman?.beforeDiscount
                                ? `Подключить за ${formatCurrency(plan.price)} руб.`
                                : 'Подключить'}
                            </Button>
                            {plan.interval === 'year' && planHuman?.beforeDiscount && (
                              <div className="text-center text-paragraph-sm text-text-secondary">
                                Экономия {formatCurrency(planHuman.beforeDiscount - plan.price)}{' '}
                                руб.
                              </div>
                            )}
                          </div>
                        )}
                      </PlanCard>
                    );
                  })}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        <div className="max-w-2xl text-text-secondary">
          <p>
            Отменить подписку можно в любой момент. Все возможности тарифа будут доступны до конца
            оплаченного периода.
          </p>
        </div>

        <div className="max-w-2xl text-text-secondary [&_a]:text-brand-primary">
          {planFooterText ? (
            // biome-ignore lint/security/noDangerouslySetInnerHtml: skip
            <div dangerouslySetInnerHTML={{__html: planFooterText}} />
          ) : (
            <p>
              Если вам хотелось бы оплатить по безналичному расчёту или появились любые другие
              вопросы связанные с оплатой напишите пожалуйста на почту{' '}
              <a target="_blank" rel="noopener noreferrer nofollow" href="mailto:hello@blesk.io">
                hello@blesk.io
              </a>
              .
            </p>
          )}
        </div>

        {isBleskStaff && <DemoRequestFrom />}
      </div>
    </Page>
  );
};
